import React from 'react';
import { useSelector } from 'react-redux';

import {
  searchModelConverter,
  SearchQueryTermType,
  SearchQueryTermWithoutPositions,
  searchRegexLanguageParser,
} from '@neptune/compound-search-domain';
import { loadFilter, normalizeCompoundOperators } from '@neptune/recent-searches-domain';
import { useAsyncValue } from '@neptune/shared/common-util';
import { createCurrentRouteParamSelector } from '@neptune/shared/routing-business-logic';
import { AttributeNameFilter, NameSearchMode } from '@neptune/shared/search-domain';

import { buildSearchQueryTerm, fromCriterionsToTerms } from './compound-search-term-utils';
import { getDefaultMode } from './get-default-mode';

const modeSelector = createCurrentRouteParamSelector('searchMode');

export type SearchQueryTermsStateResult = {
  compoundNamespace: string;
  loading: boolean;
  mode: NameSearchMode;
  searchQueryTerms: SearchQueryTermWithoutPositions[];
  textFilter: string | undefined;
};

export type SearchStateResult = SearchQueryTermsStateResult & {
  searchQuery: string;
  searchQueryTitleized: string;
  attributeNameFilter: AttributeNameFilter | undefined;
};

export function useSearchState(namespace: string): SearchStateResult {
  const compoundNamespace = `${namespace}-compound`;
  const textFilter: string | undefined = useSelector(createCurrentRouteParamSelector(namespace));

  const compoundSearchFilterKey: string | undefined = useSelector(
    createCurrentRouteParamSelector(compoundNamespace),
  );
  const nameSearchFilterKey: string | undefined = useSelector(
    createCurrentRouteParamSelector(namespace),
  );

  const resolver = React.useCallback(
    async () => loadFilter(compoundSearchFilterKey),
    [compoundSearchFilterKey],
  );
  const { value: compoundSearchFilter, loading } = useAsyncValue({ resolver });

  const compoundSearchCriterions = React.useMemo(() => {
    return normalizeCompoundOperators(compoundSearchFilter ?? []);
  }, [compoundSearchFilter]);

  const mode: NameSearchMode =
    useSelector(modeSelector) ?? getDefaultMode(compoundSearchFilterKey, nameSearchFilterKey);

  const searchQueryTerms = React.useMemo(() => {
    if (mode === NameSearchMode.REGEX) {
      return compoundSearchCriterions ? fromCriterionsToTerms(compoundSearchCriterions) : [];
    }

    return textFilter?.length && textFilter.length > 0 ? stringToSearchQueryTerms(textFilter) : [];
  }, [mode, compoundSearchCriterions, textFilter]);

  const searchQuery = React.useMemo(() => {
    return searchRegexLanguageParser.serialize(searchQueryTerms);
  }, [searchQueryTerms]);

  const searchQueryTitleized = React.useMemo(() => {
    return searchModelConverter.fromDomainToTitle(searchQueryTerms);
  }, [searchQueryTerms]);

  const attributeNameFilter = React.useMemo((): AttributeNameFilter | undefined => {
    if (searchQueryTerms.length === 0) {
      return undefined;
    }

    if (mode === NameSearchMode.SUBSTRING) {
      return searchModelConverter.fromSearchQueryToSingleAttributeNameFilter(searchQueryTerms);
    }

    return searchModelConverter.fromSearchQueryToAttributeNameFilter(searchQueryTerms);
  }, [searchQueryTerms, mode]);

  return {
    compoundNamespace,
    searchQueryTerms,
    textFilter,
    loading,
    mode,
    searchQuery,
    searchQueryTitleized,
    attributeNameFilter,
  };
}

function stringToSearchQueryTerms(value: string): SearchQueryTermWithoutPositions[] {
  return [buildSearchQueryTerm(SearchQueryTermType.CRITERION, value)];
}
